/**
 * AAPS Blog Enhancements JavaScript
 * All American Print Supply Co.
 *
 * Handles all interactive blog features:
 * - DOM repositioning (breadcrumb, mobile TOC, author box, CTA into article flow)
 * - Reading progress bar
 * - Sticky nav show/hide on scroll (top or bottom position)
 * - Sidebar TOC auto-generation and active section tracking
 * - Mobile TOC toggle
 * - FAQ accordion expand/collapse
 * - Troubleshooting cause/fix card accordion
 * - Mobile sticky CTA visibility
 *
 * No dependencies. Vanilla JS only.
 */

(function () {
  'use strict';

  // Wait for DOM
  document.addEventListener('DOMContentLoaded', function () {
    repositionElements();
    initResponsiveTables();
    initProgressBar();
    initStickyNav();
    injectSidebarTOC();
    initTOC();
    initMobileTOC();
    initFAQAccordion();
    initCauseCards();
    initMobileStickyCtaVisibility();
    initNewsletterForm();
    watchForCustomizerUpdates();
  });


  /**
   * Reposition Elements
   * Moves breadcrumb, mobile TOC, author box, and final CTA from the
   * enhancements section into the correct positions within the main
   * blog post section's DOM.
   *
   * Safe to call multiple times — cleans up previously repositioned
   * elements before re-inserting, so customizer live-preview works.
   */
  function repositionElements() {
    var articleInner = document.querySelector('.article__inner');
    var articleContent = document.querySelector('.article__content.rte');
    var pageHeader = document.querySelector('.page__header');

    // --- Breadcrumb ---
    var breadcrumbSource = document.getElementById('aaps-breadcrumb-source');
    if (breadcrumbSource && pageHeader) {
      // Remove any previously repositioned breadcrumb
      var oldBreadcrumb = document.querySelector('.page__header ~ .aaps-breadcrumb, .aaps-breadcrumb');
      if (oldBreadcrumb && oldBreadcrumb !== breadcrumbSource.querySelector('.aaps-breadcrumb')) {
        oldBreadcrumb.remove();
      }
      var breadcrumbNav = breadcrumbSource.querySelector('.aaps-breadcrumb');
      if (breadcrumbNav) {
        var clone = breadcrumbNav.cloneNode(true);
        pageHeader.parentNode.insertBefore(clone, pageHeader);
      }
      breadcrumbSource.style.display = 'none';
    }

    // --- Mobile TOC ---
    var mobileToc = document.getElementById('aaps-mobile-toc');
    if (mobileToc && articleContent) {
      articleContent.parentNode.insertBefore(mobileToc, articleContent);
      mobileToc.removeAttribute('style');
    }

    // --- Find insertion point for author box and CTA ---
    var insertionTarget = null;
    if (articleInner) {
      var commentsAnchor = articleInner.querySelector('#comments');
      var articleNav = articleInner.querySelector('.article__navigation');
      if (commentsAnchor) {
        insertionTarget = commentsAnchor;
      } else if (articleNav) {
        insertionTarget = articleNav;
      }
    }

    // --- Author Box ---
    var authorSource = document.getElementById('aaps-author-box-source');
    if (authorSource) {
      // Remove any previously placed author box in the article
      var oldAuthor = articleInner ? articleInner.querySelector('.aaps-author-box') : null;
      if (oldAuthor) oldAuthor.remove();

      var authorBox = authorSource.querySelector('.aaps-author-box');
      if (authorBox) {
        var authorClone = authorBox.cloneNode(true);
        if (insertionTarget) {
          insertionTarget.parentNode.insertBefore(authorClone, insertionTarget);
        } else if (articleInner) {
          articleInner.appendChild(authorClone);
        }
      }
      authorSource.style.display = 'none';
    }

    // --- Final CTA ---
    var ctaSource = document.getElementById('aaps-final-cta-source');
    if (ctaSource) {
      // Remove any previously placed CTA in the article
      var oldCta = articleInner ? articleInner.querySelector('.aaps-final-cta') : null;
      if (oldCta) oldCta.remove();

      var ctaBlock = ctaSource.querySelector('.aaps-final-cta');
      if (ctaBlock) {
        var ctaClone = ctaBlock.cloneNode(true);
        if (insertionTarget) {
          insertionTarget.parentNode.insertBefore(ctaClone, insertionTarget);
        } else if (articleInner) {
          articleInner.appendChild(ctaClone);
        }
      }
      ctaSource.style.display = 'none';
    }
  }

  /**
   * Watch for Shopify customizer re-renders.
   * When section settings change, the customizer replaces the section HTML.
   * We detect this and re-run repositioning so changes appear live.
   */
  function watchForCustomizerUpdates() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    var enhancementsSection = document.querySelector('[data-section-type="aaps-article-enhancements"]')
      || document.querySelector('#shopify-section-aaps-enhancements');

    if (!enhancementsSection) {
      // Try broader: find any section containing our source elements
      var authorSrc = document.getElementById('aaps-author-box-source');
      if (authorSrc) enhancementsSection = authorSrc.closest('.shopify-section');
    }

    if (!enhancementsSection) return;

    // Shopify emits section events in design mode
    document.addEventListener('shopify:section:load', function () {
      repositionElements();
    });

    document.addEventListener('shopify:section:reorder', function () {
      repositionElements();
    });

    // Also observe DOM mutations for settings changes (no section reload)
    var observer = new MutationObserver(function (mutations) {
      var shouldReposition = false;
      mutations.forEach(function (m) {
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          shouldReposition = true;
        }
      });
      if (shouldReposition) {
        repositionElements();
      }
    });

    observer.observe(enhancementsSection, { childList: true, subtree: true });
  }


  /**
   * Responsive Tables
   * Adds data-label attributes to table cells so CSS can display
   * column headers inline on mobile (card-style layout).
   */
  function initResponsiveTables() {
    var tables = document.querySelectorAll('.aaps-table-wrapper table');

    tables.forEach(function (table) {
      var headerCells = table.querySelectorAll('thead th');
      if (headerCells.length === 0) return;

      // Collect header text
      var headers = [];
      headerCells.forEach(function (th) {
        headers.push(th.textContent.trim());
      });

      // Apply data-label to each body cell
      var rows = table.querySelectorAll('tbody tr');
      rows.forEach(function (row) {
        var cells = row.querySelectorAll('td');
        cells.forEach(function (cell, index) {
          if (headers[index]) {
            cell.setAttribute('data-label', headers[index]);
          }
        });
      });
    });
  }


  /**
   * Reading Progress Bar
   * Tracks scroll position relative to article content
   */
  function initProgressBar() {
    var bar = document.querySelector('.aaps-progress-bar');
    var article = document.querySelector('.article__content.rte');

    if (!bar || !article) return;

    window.addEventListener('scroll', function () {
      var articleTop = article.offsetTop;
      var articleHeight = article.offsetHeight;
      var windowHeight = window.innerHeight;
      var scrollPosition = window.scrollY;

      var progress = Math.min(
        Math.max(
          ((scrollPosition - articleTop) / (articleHeight - windowHeight)) * 100,
          0
        ),
        100
      );

      bar.style.width = progress + '%';
      bar.setAttribute('aria-valuenow', Math.round(progress));
    });
  }


  /**
   * Sticky Nav
   * Shows after scrolling past the article header area.
   * Supports top or bottom positioning via data-sticky-position attribute.
   */
  function initStickyNav() {
    var stickyNav = document.querySelector('.aaps-sticky-nav');

    if (!stickyNav || stickyNav.classList.contains('aaps-sticky-nav--disabled')) return;

    // Find the page header / hero area
    var header = document.querySelector('.page__header') ||
                 document.querySelector('.article__image-wrapper') ||
                 document.querySelector('.article__content.rte');

    if (!header) return;

    window.addEventListener('scroll', function () {
      var headerBottom = header.offsetTop + header.offsetHeight;

      if (window.scrollY > headerBottom) {
        stickyNav.classList.add('aaps-visible');
        stickyNav.setAttribute('aria-hidden', 'false');
      } else {
        stickyNav.classList.remove('aaps-visible');
        stickyNav.setAttribute('aria-hidden', 'true');
      }
    });
  }


  /**
   * Inject Sidebar TOC
   * Creates a sticky TOC card and prepends it to the Warehouse theme's
   * existing .blog-sidebar element, or creates a sidebar if none exists.
   */
  function injectSidebarTOC() {
    var articleContent = document.querySelector('.article__content.rte');

    if (!articleContent) return;

    var headings = articleContent.querySelectorAll('h2[id]');

    if (headings.length < 3) return; // Only show TOC for 3+ sections

    // Build unified sidebar — one clean card with sections divided by lines
    var tocHtml = '<div class="aaps-toc-sidebar">';

    // TOC section
    tocHtml += '<div class="aaps-toc-card">';
    tocHtml += '<h4>On this page</h4>';
    tocHtml += '<nav role="navigation" aria-label="Table of Contents">';
    tocHtml += '<ul class="aaps-toc-list" id="aaps-sidebar-toc-list">';
    tocHtml += '</ul></nav></div>';

    // Divider
    tocHtml += '<hr class="aaps-sidebar-divider">';

    // Newsletter — compact inline form
    tocHtml += '<div class="aaps-sidebar-newsletter">';
    tocHtml += '<h4>Newsletter</h4>';
    tocHtml += '<p>Equipment tips &amp; business advice. No spam.</p>';
    tocHtml += '<form id="aaps-newsletter-form" class="aaps-newsletter-form" novalidate>';
    tocHtml += '<input type="email" name="contact[email]" class="aaps-newsletter-input" placeholder="Your email" aria-label="Email for newsletter" required>';
    tocHtml += '<input type="hidden" name="contact[tags]" value="newsletter">';
    tocHtml += '<button type="submit" class="aaps-btn-subscribe">Go</button>';
    tocHtml += '</form>';
    tocHtml += '<p class="aaps-newsletter-msg" style="display:none"></p>';
    tocHtml += '</div>';

    // Divider
    tocHtml += '<hr class="aaps-sidebar-divider">';

    // CTA — simple button
    tocHtml += '<div class="aaps-sidebar-cta">';
    tocHtml += '<a href="/pages/contact-sales" class="aaps-btn-cta">Talk to a Print Expert →</a>';
    tocHtml += '</div>';

    tocHtml += '</div>';

    // Find the Warehouse theme sidebar or create one
    var sidebar = document.querySelector('.blog-sidebar');

    if (sidebar) {
      // Prepend the TOC to existing sidebar
      sidebar.insertAdjacentHTML('afterbegin', tocHtml);
    } else {
      // Create sidebar alongside the blog-post content
      var blogPost = document.querySelector('.blog-post');
      var blogContainer = document.querySelector('.blog-container');

      if (blogContainer && blogPost) {
        var sidebarEl = document.createElement('div');
        sidebarEl.className = 'blog-sidebar aaps-injected-sidebar hidden-tablet';
        sidebarEl.innerHTML = tocHtml;
        blogContainer.appendChild(sidebarEl);
      }
    }
  }


  /**
   * Sidebar TOC
   * Auto-generates TOC from H2 headings and tracks active section
   */
  function initTOC() {
    var articleContent = document.querySelector('.article__content.rte');
    var sidebarTocList = document.querySelector('#aaps-sidebar-toc-list');

    if (!articleContent) return;

    // Find all H2s inside the article content
    var headings = articleContent.querySelectorAll('h2[id]');

    if (headings.length === 0) return;

    // Build TOC links for sidebar
    if (sidebarTocList) {
      headings.forEach(function (heading) {
        var li = document.createElement('li');
        var link = document.createElement('a');
        link.href = '#' + heading.id;
        link.className = 'aaps-toc-link';
        link.textContent = heading.textContent.replace(/^\d+\.\s*/, '');
        li.appendChild(link);
        sidebarTocList.appendChild(li);
      });
    }

    // Build TOC links for mobile
    var mobileTocList = document.querySelector('#aaps-mobile-toc-list');
    if (mobileTocList) {
      headings.forEach(function (heading) {
        var li = document.createElement('li');
        var link = document.createElement('a');
        link.href = '#' + heading.id;
        link.className = 'aaps-toc-link';
        link.textContent = heading.textContent.replace(/^\d+\.\s*/, '');
        li.appendChild(link);
        mobileTocList.appendChild(li);
      });
    }

    // Active section tracking — scroll-based for reliable bi-directional updates
    var allTocLinks = document.querySelectorAll('.aaps-toc-link');

    if (allTocLinks.length === 0) return;

    var headingIds = [];
    headings.forEach(function (h) { headingIds.push(h.id); });

    var currentActiveId = null;
    var scrollOffset = 100; // px from top to consider "in section"

    function updateActiveTocLink() {
      var scrollY = window.scrollY || window.pageYOffset;
      var activeId = null;

      // Walk headings top-to-bottom; the last one whose top is above
      // the scroll position + offset is the current section
      for (var i = 0; i < headingIds.length; i++) {
        var el = document.getElementById(headingIds[i]);
        if (el && el.getBoundingClientRect().top <= scrollOffset) {
          activeId = headingIds[i];
        }
      }

      // If nothing matched (user is above the first heading), clear all
      if (activeId === currentActiveId) return;
      currentActiveId = activeId;

      allTocLinks.forEach(function (link) {
        link.classList.remove('aaps-active');
      });

      if (activeId) {
        var activeLinks = document.querySelectorAll(
          'a.aaps-toc-link[href="#' + activeId + '"]'
        );
        activeLinks.forEach(function (link) {
          link.classList.add('aaps-active');
        });
      }
    }

    // Throttle scroll handler for performance
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          updateActiveTocLink();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Run once on load
    updateActiveTocLink();

    // Smooth scroll on TOC link click
    allTocLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var targetId = link.getAttribute('href').substring(1);
        var target = document.getElementById(targetId);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });

          // Close mobile TOC after clicking
          var mobileTocHeader = document.querySelector('.aaps-mobile-toc-header');
          var mobileTocBody = document.querySelector('.aaps-mobile-toc-body');
          if (mobileTocHeader && mobileTocHeader.classList.contains('active')) {
            mobileTocHeader.classList.remove('active');
            mobileTocHeader.setAttribute('aria-expanded', 'false');
            mobileTocBody.style.maxHeight = null;
          }
        }
      });
    });
  }


  /**
   * Mobile TOC Toggle
   * Expand/collapse the mobile table of contents
   */
  function initMobileTOC() {
    var tocHeader = document.querySelector('.aaps-mobile-toc-header');
    var tocBody = document.querySelector('.aaps-mobile-toc-body');

    if (!tocHeader || !tocBody) return;

    tocHeader.addEventListener('click', function () {
      var isOpen = tocHeader.classList.contains('active');

      tocHeader.classList.toggle('active');

      if (!isOpen) {
        tocBody.style.maxHeight = tocBody.scrollHeight + 'px';
        tocHeader.setAttribute('aria-expanded', 'true');
      } else {
        tocBody.style.maxHeight = null;
        tocHeader.setAttribute('aria-expanded', 'false');
      }
    });
  }


  /**
   * FAQ Accordion
   * Handles .aaps-faq-question click to expand/collapse answers
   */
  function initFAQAccordion() {
    var questions = document.querySelectorAll('.aaps-faq-question');

    if (questions.length === 0) return;

    questions.forEach(function (button) {
      button.addEventListener('click', function () {
        var answer = button.nextElementSibling;
        var isOpen = button.classList.contains('active');

        // Close all other open answers
        questions.forEach(function (other) {
          if (other !== button && other.classList.contains('active')) {
            other.classList.remove('active');
            other.setAttribute('aria-expanded', 'false');
            other.nextElementSibling.style.maxHeight = null;
          }
        });

        // Toggle this answer
        button.classList.toggle('active');

        if (!isOpen) {
          answer.style.maxHeight = answer.scrollHeight + 'px';
          button.setAttribute('aria-expanded', 'true');
        } else {
          answer.style.maxHeight = null;
          button.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }


  /**
   * Troubleshooting Cause/Fix Cards
   * Handles .aaps-cause-header click to expand/collapse cause details
   */
  function initCauseCards() {
    var headers = document.querySelectorAll('.aaps-cause-header');

    if (headers.length === 0) return;

    headers.forEach(function (header, index) {
      // First card expanded by default
      if (index === 0) {
        header.classList.add('active');
        var firstBody = header.nextElementSibling;
        if (firstBody) {
          firstBody.style.maxHeight = firstBody.scrollHeight + 'px';
        }
      }

      header.addEventListener('click', function () {
        var body = header.nextElementSibling;
        var isOpen = header.classList.contains('active');

        header.classList.toggle('active');

        if (!isOpen) {
          body.style.maxHeight = body.scrollHeight + 'px';
        } else {
          body.style.maxHeight = null;
        }
      });
    });
  }


  /**
   * Mobile Sticky CTA Visibility
   * Hides the mobile sticky CTA when the final CTA block is visible.
   * Also hides it when the bottom sticky nav is active to avoid overlap.
   */
  function initMobileStickyCtaVisibility() {
    var mobileCta = document.querySelector('#aaps-mobile-sticky-cta');
    var finalCta = document.querySelector('#aaps-final-cta');
    var stickyNav = document.querySelector('.aaps-sticky-nav');
    var isBottomNav = stickyNav && stickyNav.classList.contains('aaps-sticky-nav--bottom');

    if (!mobileCta) return;

    // Hide mobile CTA when bottom sticky nav is present (they'd overlap)
    if (isBottomNav && stickyNav && !stickyNav.classList.contains('aaps-sticky-nav--disabled')) {
      mobileCta.style.display = 'none';
    }

    // Hide mobile CTA when final CTA is visible
    if (finalCta) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            mobileCta.style.display = 'none';
          } else {
            // Only re-show if bottom nav isn't handling the CTA
            if (!isBottomNav) {
              mobileCta.style.display = '';
            }
          }
        });
      });

      observer.observe(finalCta);
    }
  }


  /**
   * Newsletter form — submits to Shopify /contact endpoint
   * Creates a customer with "newsletter" tag for email marketing.
   */
  function initNewsletterForm() {
    var form = document.getElementById('aaps-newsletter-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var emailInput = form.querySelector('input[name="contact[email]"]');
      var btn = form.querySelector('.aaps-btn-subscribe');
      var msg = form.parentElement.querySelector('.aaps-newsletter-msg');
      var email = emailInput.value.trim();

      // Basic email validation
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        msg.textContent = 'Please enter a valid email address.';
        msg.style.display = 'block';
        msg.style.color = '#e53e3e';
        return;
      }

      // Disable while submitting
      btn.disabled = true;
      btn.textContent = 'Sending...';
      msg.style.display = 'none';

      // Build form data for Shopify /contact endpoint
      var formData = new FormData();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');
      formData.append('contact[email]', email);
      formData.append('contact[tags]', 'newsletter');

      fetch('/contact', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'text/html'
        }
      })
      .then(function (response) {
        if (response.ok || response.redirected) {
          // Success — Shopify returns a redirect on successful submission
          msg.textContent = 'You\'re subscribed! Check your inbox.';
          msg.style.display = 'block';
          msg.style.color = '#38a169';
          emailInput.value = '';
          btn.textContent = 'Subscribed ✓';
        } else {
          throw new Error('Submission failed');
        }
      })
      .catch(function () {
        msg.textContent = 'Something went wrong. Please try again.';
        msg.style.display = 'block';
        msg.style.color = '#e53e3e';
        btn.textContent = 'Subscribe';
        btn.disabled = false;
      });
    });
  }
})();
